import { Component, inject, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { User } from '../../../../../_shared/models';
import {
  FuelToReconcile,
  PaginatedFuelToReconcileRequest,
  ReconcileFuelSlipRequest,
  CurrencyOption,
} from '../../../models/fuel-recon.model';
import { CommonModule } from '@angular/common';
import { PaginationComponent } from '../../../../../_shared/components/pagination/pagination.component';
import { FuelReconciliationService } from '../../../services/fuel-reconciliation.service';
import { Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AuthService, FilterService } from '../../../../../_shared/services';
import { FormsModule } from '@angular/forms';
import { toSeconds } from '../../../helpers/timezone.helper';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../../../../_shared/components/states/error-state/error-state.component';
import { isEmptyObject } from '../../../../../_shared/helpers/object.helper';
import { CountryFlagComponent } from "../../../../../_shared/components/country-flag/country-flag.component";
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-fuel-to-reconcile-table',
  standalone: true,
  imports: [
    TranslocoDirective,
    TranslocoPipe,
    CommonModule,
    PaginationComponent,
    FormsModule,
    EmptyStateComponent,
    ErrorStateComponent,
    CountryFlagComponent
  ],
  templateUrl: './fuel-to-reconcile-table.component.html',
  styleUrl: './fuel-to-reconcile-table.component.scss',
})
export class FuelToReconcileTableComponent implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  @Output() slipReconciled = new EventEmitter<void>();
  public isLoading: boolean = false;
  public fuelToReconcile: FuelToReconcile[];
  public totalItems: number = 0;
  public numberOfPages: number = 0;
  public currentItems: number = 0;
  public currentPage: number = 1;
  public expandedRowIndex: number | null = null;
  public reconcileFuelSlipRequest: ReconcileFuelSlipRequest = {} as ReconcileFuelSlipRequest;
  public costType: string | null;
  public hasError: boolean = false;
  public errorMessage: string;
  public currencies: CurrencyOption[] = [];
  public filteredCurrencies: CurrencyOption[] = [];
  public selectedCurrency: CurrencyOption | null = null;
  public currencySearchTerm: string = '';
  public selectedFile: File | null = null;
  private destroySubject: Subject<void> = new Subject<void>();
  private paginatedRequest: PaginatedFuelToReconcileRequest;
  private cancellation: Subject<void> = new Subject<void>();
  private user: User;
  private defaultUserCurrency: string = '';
  private sortColumn: keyof FuelToReconcile | null = null;
  private sortDirection: 'asc' | 'desc' = 'asc';
  private currencyOptionsCancelSubject: Subject<void> = new Subject<void>();

  constructor(
    private fuelReconService: FuelReconciliationService,
    private toastr: ToastrService,
    private authService: AuthService,
    private errorService: ErrorHandlerService,
    private filterService: FilterService
  ) {
    this.user = this.authService.getUser();
    this.paginatedRequest = {
      pageSize: 5,
      pageNumber: 1,
      userId: this.user.id,
      utcOffset: 0,
      startDate: new Date(),
      endDate: new Date(),
      fuelIntake: '',
    };
    this.defaultUserCurrency = this.authService.getUserSettings()!.currencyCode || 'ZAR';
  }

  ngOnInit(): void {
    this.loadCurrenciesAsync();
    this.filterService.filterUpdated$
      .pipe(takeUntil(this.destroySubject))
      .subscribe(async (filter) => {
        if (!isEmptyObject(filter)) {
          this.paginatedRequest.startDate = filter.startDate;
          this.paginatedRequest.endDate = filter.endDate;
          await this.getPaginatedFuelToReconcileByAccountIdAsync()
        }
      });

    this.filterService.searchTerm$
      .pipe(takeUntil(this.destroySubject))
      .subscribe(async (searchTerm) => {
        this.paginatedRequest.fuelIntake = searchTerm;
        this.paginatedRequest.pageNumber = 1;
        this.currentPage = 1;
        await this.getPaginatedFuelToReconcileByAccountIdAsync();
      });
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
    this.cancellation.next();
    this.cancellation.complete();
    this.currencyOptionsCancelSubject.next();
    this.currencyOptionsCancelSubject.complete();
  }

  public async createPagination(page: number): Promise<void> {
    if (page > this.numberOfPages || page < 1) {
      return;
    }

    this.paginatedRequest.pageNumber = page;
    this.currentPage = page;

    await this.getPaginatedFuelToReconcileByAccountIdAsync();
  }

  public async getPaginatedFuelToReconcileByAccountIdAsync(): Promise<void> {
    try {
      this.cancellation.next();
      this.fuelToReconcile = [];
      this.isLoading = true;
      this.getUtcOffsetFromFuelSlipUploadPreference();
      const response = await this.fuelReconService.getPaginatedFuelToReconcileByAccountIdAsync(
        this.paginatedRequest,
        this.cancellation
      );
      this.fuelToReconcile = response.data?.results ?? [];
      this.totalItems = response.data?.totalCount ?? 0;
      this.currentItems = Math.min(
        this.currentPage * this.paginatedRequest.pageSize,
        this.totalItems
      );
      this.numberOfPages = response.data?.totalPages ?? 0;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: (message) => {
          this.errorMessage = message;
          this.hasError = true;
        }
      });
    } finally {
      this.isLoading = false;
    }
  }

  public toggleRowExpansion(index: number | null): void {
    this.expandedRowIndex = this.expandedRowIndex === index ? null : index;
    this.reconcileFuelSlipRequest = {} as ReconcileFuelSlipRequest;
    this.costType = null;
    this.currencySearchTerm = '';
    this.selectedFile = null;
    this.filteredCurrencies = [...this.currencies];
    this.setDefaultCurrencyFromLocalStorage();
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] ?? null;
  }

  public async reconcileFuelSlipAsync(fuel: FuelToReconcile): Promise<void> {
    try {
      this.isLoading = true;
      this.fuelToReconcile = [];
      this.cancellation.next();
      this.reconcileFuelSlipRequest.assetId = fuel.assetId;
      this.reconcileFuelSlipRequest.assetName = fuel.assetName;
      this.reconcileFuelSlipRequest.fuelSlipId = fuel.fuelSlipId;
      this.reconcileFuelSlipRequest.isTotalCost = this.costType === 'Total Cost';
      this.reconcileFuelSlipRequest.createdBy = this.user.login;
      this.reconcileFuelSlipRequest.currencyId = this.selectedCurrency!.id;

      await this.fuelReconService.reconcileFuelSlipAsync(this.reconcileFuelSlipRequest, this.selectedFile, this.cancellation);

      this.toastr.success(this.transloco.translate('fuelReconciliation.messages.slipReconciled'), this.transloco.translate('fuelReconciliation.messages.success'));
      this.slipReconciled.emit();
    } catch (error: any) {
      if (error.name !== 'EmptyError') {
        this.errorService.handleError(error, {
          callback: (message) => this.toastr.error(message)
        });
      }
    } finally {
      this.isLoading = false;
      this.toggleRowExpansion(null);
      await this.getPaginatedFuelToReconcileByAccountIdAsync();
    }
  }

  public selectCostType(type: string): void {
    this.costType = type;
  }

  public sortByColumn(column: keyof FuelToReconcile): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    this.fuelToReconcile = [...this.fuelToReconcile].sort((a, b) => {
      const valueA = a[column];
      const valueB = b[column];

      if (valueA instanceof Date && valueB instanceof Date) {
        return this.sortDirection === 'asc'
          ? valueA.getTime() - valueB.getTime()
          : valueB.getTime() - valueA.getTime();
      }

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return this.sortDirection === 'asc'
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return this.sortDirection === 'asc'
          ? valueA - valueB
          : valueB - valueA;
      }

      return 0;
    });
  }

  public async loadCurrenciesAsync(): Promise<void> {
    try {
      const response = await this.fuelReconService.getCurrencyOptionsAsync(this.currencyOptionsCancelSubject);

      this.currencies = response.data ?? [];
      this.filteredCurrencies = [...this.currencies];
      this.setDefaultCurrencyFromLocalStorage();
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: (message) => {
          this.errorMessage = message;
          this.hasError = true;
        }
      });
    }
  }

  public filterCurrencies(): void {
    const searchTerm = this.currencySearchTerm.toLowerCase();
    this.filteredCurrencies = this.currencies.filter(
      (currency) =>
        currency.name.toLowerCase().includes(searchTerm) ||
        currency.code.toLowerCase().includes(searchTerm)
    );
  }

  public selectCurrency(currency: CurrencyOption): void {
    this.selectedCurrency = currency;
    this.currencySearchTerm = '';
    this.filteredCurrencies = [...this.currencies];
  }

  private setDefaultCurrencyFromLocalStorage(): void {
    if (!this.currencies || this.currencies.length === 0) {
      return;
    }

    const matchingCurrency = this.currencies.find(
      c => c.code === this.defaultUserCurrency
    );

    this.selectedCurrency = matchingCurrency
      || this.currencies.find(c => c.code === 'ZAR')
      || this.currencies[0];
  }

  private getUtcOffsetFromFuelSlipUploadPreference(): void {
    this.fuelReconService.getFuelSlipUploadPreferenceAsync(this.cancellation)
      .then(response => this.paginatedRequest.utcOffset = response.data?.utcOffset)
      .catch(() => this.paginatedRequest.utcOffset = -toSeconds(new Date().getTimezoneOffset()));
  }
}